'use client'

import { useEffect } from 'react'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useMutation, useQuery } from '@apollo/client'
import {
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { SheetScrollableContent } from '@/components/ui/sheet-scrollable-content'
import '@/components/styles/Form.css'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import {
  CREATE_ONE_CUSTOMER_SUB_ACCOUNT,
  UPDATE_ONE_CUSTOMER_SUB_ACCOUNT,
  FIND_UNIQUE_CUSTOMER_SUB_ACCOUNT,
  FIND_MANY_CUSTOMER_SUB_ACCOUNT,
} from './customerSubAccountQueries'
import { FIND_UNIFIED_CUSTOMER_ACCOUNTS_AND_LOCATIONS } from '../CustomerAccounts/customerAccountQueries'
import { CustomerSubAccountFormValues } from './types'

const formSchema = z.object({
  name: z.string().min(1, 'Location name is required'),
}) satisfies z.ZodType<CustomerSubAccountFormValues>

interface CustomerSubAccountFormProps {
  data: {
    id?: string
    customerId: number
  }
  onDirtyChange?: (dirty: boolean) => void
  onRequestClose?: () => void
}

export default function CustomerSubAccountForm({
  data,
  onDirtyChange,
  onRequestClose,
}: CustomerSubAccountFormProps) {
  const { id, customerId } = data

  const {
    data: { findUniqueCustomerSubAccount: customerSubAccountData } = {},
    loading: loadingData,
  } = useQuery(FIND_UNIQUE_CUSTOMER_SUB_ACCOUNT, {
    variables: { id: id! },
    fetchPolicy: 'network-only',
    skip: !id,
  })

  const form = useForm<CustomerSubAccountFormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      name: '',
    },
  })

  const [updateCustomerSubAccount, { loading: updateLoading }] = useMutation(
    UPDATE_ONE_CUSTOMER_SUB_ACCOUNT,
    {
      onError: e => {
        toast.error('Failed to update location')
      },
      onCompleted: () => {
        toast.success('Location updated successfully')
        form.reset()
        if (onRequestClose) {
          onRequestClose()
        }
      },
      refetchQueries: [
        {
          query: FIND_MANY_CUSTOMER_SUB_ACCOUNT,
          variables: { customerId },
        },
        {
          query: FIND_UNIFIED_CUSTOMER_ACCOUNTS_AND_LOCATIONS,
          variables: { customerId },
        },
      ],
    }
  )

  const [createCustomerSubAccount, { loading: createLoading }] = useMutation(
    CREATE_ONE_CUSTOMER_SUB_ACCOUNT,
    {
      onError: e => {
        console.error('Error creating location:', e)
        toast.error('Failed to create location')
      },
      onCompleted: response => {
        toast.success('Location created successfully')
        form.reset()
        if (onRequestClose) {
          onRequestClose()
        }
      },
      refetchQueries: [
        {
          query: FIND_MANY_CUSTOMER_SUB_ACCOUNT,
          variables: { customerId },
        },
      ],
    }
  )

  function onSubmit(values: CustomerSubAccountFormValues) {
    if (id) {
      updateCustomerSubAccount({
        variables: {
          where: { id },
          data: { name: { set: values.name } },
        },
      })
    } else {
      createCustomerSubAccount({
        variables: {
          customerId,
          name: values.name,
        },
      })
    }
  }

  // Handle form submission with event prevention
  const handleFormSubmit = (event: React.FormEvent) => {
    // Prevent the form submission from bubbling up to parent forms
    event.preventDefault()
    event.stopPropagation()

    // Call the react-hook-form's handleSubmit
    form.handleSubmit(onSubmit)(event)
  }

  useEffect(() => {
    console.log('CustomerSubAccountForm useEffect:', {
      id,
      customerSubAccountData,
      loadingData,
      hasData: !!customerSubAccountData,
      name: customerSubAccountData?.name,
    })

    if (customerSubAccountData) {
      console.log('Resetting form with data:', customerSubAccountData.name)
      form.reset({
        name: customerSubAccountData.name || '',
      })
    } else if (!id) {
      // Reset form to empty values when creating a new location
      console.log('Resetting form to empty values')
      form.reset({
        name: '',
      })
    }
  }, [customerSubAccountData, form, id])

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(form.formState.isDirty)
    }
  }, [form.formState.isDirty, onDirtyChange])

  return (
    <SheetContent className="sheet-content flex flex-col">
      <SheetHeader className="flex-shrink-0">
        <SheetTitle>{id ? 'Edit Location' : 'Add Location'}</SheetTitle>
        {id && <SheetDescription>ID: {id}</SheetDescription>}
      </SheetHeader>
      <SheetScrollableContent>
        <Form {...form}>
          <form
            onSubmit={handleFormSubmit}
            className="space-y-4 max-w-3xl mx-auto py-5"
          >
            <div className="grid gap-4 py-4">
              <InputFieldContainer
                label="Location Name"
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input
                        placeholder="Store/Shop name"
                        type="text"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                disabled={loadingData || updateLoading || createLoading}
              >
                {updateLoading || createLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    {id ? 'Updating...' : 'Creating...'}
                  </div>
                ) : id ? (
                  'Update Location'
                ) : (
                  'Create Location'
                )}
              </Button>
            </div>
          </form>
        </Form>
      </SheetScrollableContent>
    </SheetContent>
  )
}
